import type { DocumentByPathResult } from './view-document';

import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  IconButton,
  Input,
  Link,
  Menu,
  Popover,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Fragment,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  FiChevronsDown,
  FiChevronsUp,
  FiMoreVertical,
  FiRefreshCw,
  FiRotateCcw,
} from 'react-icons/fi';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { DocumentMessagesDrawer } from './document-messages-drawer';

import { CenteredDialogContent } from '#src/components/ui/centered-dialog-content';
import { toaster } from '#src/components/ui/toaster';
import { UPSERT_DOCUMENT } from '#src/graphql/mutations/upsert-document';
import { useAppConfig } from '#src/providers/app-config-provider';
import { useAppSettings } from '#src/providers/app-settings-provider';
import { usePageHeader } from '#src/providers/page-layout-provider';
import {
  DOCUMENT_PATH_DESCRIPTION,
  isValidDocumentPath,
} from '#src/utils/document-path';
import { formatDocumentName } from '#src/utils/format-document-name';

/** Runs Apollo `useSuspenseQuery` refetch inside `startTransition` so the Suspense boundary does not swap to the loading fallback during save/rename. */
function refetchInTransition(refetch: () => Promise<unknown>) {
  return new Promise<void>((resolve, reject) => {
    startTransition(() => {
      void refetch().then(() => resolve(), reject);
    });
  });
}

interface DocumentChildrenButtonProps {
  parentPath: string;
  items: Array<{ id: string; path: string }>;
  hasMore: boolean;
}

function DocumentChildrenButton({
  parentPath,
  items,
  hasMore,
}: DocumentChildrenButtonProps) {
  const countLabel = hasMore ? `${items.length}+` : `${items.length}`;

  return (
    <Popover.Root positioning={{ placement: 'bottom-start' }} size="sm">
      <Popover.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          color="fg.muted"
          fontWeight="normal"
          fontSize="sm"
          px={2}
          minW={0}
          title="Child documents"
        >
          🔗 {countLabel}
        </Button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="320px"
            maxH="300px"
            overflowY="auto"
            p={3}
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={1} align="stretch">
              <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={1}>
                Child documents
              </Text>
              {items.map((child: { id: string; path: string }) => (
                <Link
                  key={child.id}
                  asChild
                  display="block"
                  fontSize="sm"
                  color="blue.600"
                  _hover={{ textDecoration: 'underline' }}
                >
                  <RouterLink to={`/documents/${child.path}`}>
                    {formatDocumentName(
                      getRelativePath(parentPath, child.path),
                    ).join(' › ')}
                  </RouterLink>
                </Link>
              ))}
              {hasMore && (
                <Text fontSize="xs" color="fg.subtle" fontStyle="italic">
                  More than {items.length} children (showing first{' '}
                  {items.length})
                </Text>
              )}
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

function getRelativePath(parentPath: string, childPath: string): string {
  if (!parentPath) return childPath;
  if (childPath === parentPath) return '';
  const prefix = parentPath + '/';
  return childPath.startsWith(prefix)
    ? childPath.slice(prefix.length)
    : childPath;
}

function DocumentBreadcrumb({
  path,
  placeholderTitleMuted = false,
}: {
  path: string;
  /** Match editor placeholder: soften title until the user edits a new path. */
  placeholderTitleMuted?: boolean;
}) {
  const titleColor = placeholderTitleMuted ? 'fg.muted' : 'fg';
  const segments = useMemo(() => {
    const rawSegments = path.split('/').filter((part) => part.length > 0);
    const formatted = formatDocumentName(path);
    const result: Array<{ label: string; accumulated: string }> = [];
    let accumulated = '';

    rawSegments.forEach((segment, index) => {
      accumulated = accumulated ? `${accumulated}/${segment}` : segment;
      result.push({
        label: formatted[index] ?? segment,
        accumulated,
      });
    });

    return result;
  }, [path]);

  if (segments.length === 0) {
    const fallback = formatDocumentName(path).join(' > ') || path;
    return (
      <Text fontSize="lg" fontWeight="semibold" color={titleColor}>
        {fallback}
      </Text>
    );
  }

  return (
    <HStack gap={1} align="center" flexWrap="wrap">
      {segments.map((segment, index) => {
        const isLast = index === segments.length - 1;
        return (
          <Fragment key={segment.accumulated}>
            {isLast ? (
              <Text fontSize="lg" fontWeight="semibold" color={titleColor}>
                {segment.label}
              </Text>
            ) : (
              <Link
                asChild
                fontSize="lg"
                fontWeight="semibold"
                color="blue.600"
                _hover={{ textDecoration: 'underline' }}
              >
                <RouterLink to={`/documents/${segment.accumulated}`}>
                  {segment.label}
                </RouterLink>
              </Link>
            )}
            {!isLast && (
              <Text fontSize="lg" fontWeight="semibold" color="fg.subtle">
                ›
              </Text>
            )}
          </Fragment>
        );
      })}
    </HStack>
  );
}

interface RenameDocumentFormProps {
  currentPath: string;
  getCurrentValue: () => string;
  onRename: (newPath: string, currentValue: string) => Promise<void>;
  onRequestClose: () => void;
  /** False when the popover or dialog is closed; resets path when opening. */
  active: boolean;
  /** Omit title lines when a dialog already provides a heading. */
  embedded?: boolean;
}

function RenameDocumentForm({
  currentPath,
  getCurrentValue,
  onRename,
  onRequestClose,
  active,
  embedded = false,
}: RenameDocumentFormProps) {
  const { documentPathRegex } = useAppSettings();
  const [renamePath, setRenamePath] = useState(currentPath);

  useEffect(() => {
    if (active) setRenamePath(currentPath);
  }, [active, currentPath]);

  const trimmedRenamePath = renamePath.trim();
  const isRenamePathValid = trimmedRenamePath
    ? isValidDocumentPath(trimmedRenamePath, documentPathRegex)
    : false;

  const handleRename = async () => {
    if (!trimmedRenamePath || !isRenamePathValid) return;
    try {
      await onRename(trimmedRenamePath, getCurrentValue());
      onRequestClose();
    } catch {
      // Parent shows toaster; keep surface open
    }
  };

  return (
    <VStack gap={3} align="stretch">
      {!embedded && (
        <>
          <Text fontSize="sm" fontWeight="semibold" color="fg">
            Rename document
          </Text>
          <Text fontSize="xs" color="fg.muted">
            Enter the new path for this document.
          </Text>
        </>
      )}
      <input
        placeholder="e.g. folder/doc-name or path (with parens)"
        value={renamePath}
        onChange={(e) => setRenamePath(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void handleRename();
          if (e.key === 'Escape') onRequestClose();
        }}
      />
      {trimmedRenamePath && !isRenamePathValid && (
        <Text fontSize="xs" color="red.500">
          {DOCUMENT_PATH_DESCRIPTION}
        </Text>
      )}
      <HStack gap={2} justify="flex-end">
        <Button size="sm" variant="ghost" onClick={onRequestClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => void handleRename()}
          disabled={!trimmedRenamePath || !isRenamePathValid}
        >
          Rename
        </Button>
      </HStack>
    </VStack>
  );
}

interface ViewDocumentHeaderProps {
  document: DocumentByPathResult;
  path: string;
  displayPath: string;
  documentId?: string;
  isNewDocument: boolean;
  /** Muted app header title while editor shows unsaved-path placeholder. */
  editorPlaceholderMuted?: boolean;
  childrenItems: Array<{ id: string; path: string }>;
  childrenHasMore: boolean;
  onNavigateToHistory: () => void;
  hasChanges: boolean;
  /** Bumps when editor JSON changes while dirty; drives trailing auto-save debounce. */
  dirtyEditGeneration: number;
  getCurrentValue: () => string;
  refetch: () => Promise<unknown>;
  onAfterSave: (savedValue: string) => void;
  /** Restore editor to the last saved / loaded document value (discards unsaved edits). */
  onRevertToSaved: () => void;
  isCreateSubpageOpen: boolean;
  onCreateSubpageOpenChange: (open: boolean) => void;
  /** Matches document editor mobile breakpoint (e.g. 650px). */
  isMobileEditorLayout: boolean;
  composerFooterVisible: boolean;
  onComposerFooterToggle: () => void;
}

export function ViewDocumentHeader({
  document,
  path,
  displayPath,
  documentId,
  isNewDocument,
  editorPlaceholderMuted = false,
  childrenItems,
  childrenHasMore,
  onNavigateToHistory,
  hasChanges,
  dirtyEditGeneration,
  getCurrentValue,
  refetch,
  onAfterSave,
  onRevertToSaved,
  isCreateSubpageOpen,
  onCreateSubpageOpenChange,
  isMobileEditorLayout,
  composerFooterVisible,
  onComposerFooterToggle,
}: ViewDocumentHeaderProps) {
  const { setTitle, actionsRef } = usePageHeader();
  const { documentPathRegex } = useAppSettings();
  const navigate = useNavigate();
  const {
    removeRecentlyOpenedDocumentById,
    config,
    setDocumentAutoSaveEnabled,
  } = useAppConfig();
  const { documentAutoSaveEnabled: autoSaveEnabled } = config;
  const [upsertDocument, { loading: saving }] = useMutation(UPSERT_DOCUMENT);
  const hasChangesRef = useRef(hasChanges);
  const autoSaveEnabledRef = useRef(autoSaveEnabled);
  const [isMessagesDrawerOpen, setIsMessagesDrawerOpen] = useState(false);
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [createSubpageSegment, setCreateSubpageSegment] = useState('');
  const getCurrentValueRef = useRef(getCurrentValue);
  const [rawValueSnapshot, setRawValueSnapshot] = useState<string>('');
  const saveInFlightRef = useRef(false);
  const handleSaveRef = useRef<
    (options?: { silent?: boolean }) => Promise<void>
  >(() => Promise.resolve());

  // Keep a stable ref to the current editor value getter; prevents heavy computations
  // from running on every parent render while the modal is open.
  useEffect(() => {
    getCurrentValueRef.current = getCurrentValue;
  }, [getCurrentValue]);

  // Snapshot the raw value once when the modal opens for a consistent preview/copy.
  useEffect(() => {
    if (!isAdvancedModalOpen) return;
    try {
      setRawValueSnapshot(getCurrentValueRef.current());
    } catch {
      setRawValueSnapshot('');
    }
  }, [isAdvancedModalOpen]);

  useEffect(() => {
    if (isCreateSubpageOpen) setCreateSubpageSegment('');
  }, [isCreateSubpageOpen]);

  const hasChildren = childrenItems.length > 0;
  const documentPathForRename =
    document?.__typename === 'Document' ? document.path : displayPath;

  const formattedName = formatDocumentName(displayPath).join(' > ');

  const trimmedSegment = createSubpageSegment.trim();
  const fullCreateSubpagePath = displayPath
    ? `${displayPath}/${trimmedSegment}`
    : trimmedSegment;
  const isCreateSubpagePathValid =
    !!trimmedSegment &&
    isValidDocumentPath(fullCreateSubpagePath, documentPathRegex);

  const handleCreateSubpage = useCallback(() => {
    const trimmed = trimmedSegment;
    const fullPath = displayPath ? `${displayPath}/${trimmed}` : trimmed;
    if (!trimmed) return;

    onCreateSubpageOpenChange(false);
    setCreateSubpageSegment('');
    navigate(`/documents/${fullPath}`);
  }, [trimmedSegment, displayPath, navigate, onCreateSubpageOpenChange]);

  const handleRename = useCallback(
    async (newPath: string, currentValue: string) => {
      if (document?.__typename !== 'Document') return;
      try {
        await upsertDocument({
          variables: {
            input: {
              id: document.id,
              path: newPath,
              value: currentValue,
            },
          },
        });
        toaster.create({
          title: 'Success',
          description: 'Document renamed.',
        });
        removeRecentlyOpenedDocumentById(document.id);
        await refetchInTransition(refetch);
        navigate(`/documents/${newPath}`, { replace: true });
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    },
    [
      document,
      navigate,
      refetch,
      removeRecentlyOpenedDocumentById,
      upsertDocument,
    ],
  );

  const handleSave = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      if (saveInFlightRef.current) return;
      saveInFlightRef.current = true;
      const value = getCurrentValue();
      try {
        const { data } = await upsertDocument({
          variables: {
            input: {
              id: documentId ?? path,
              path: isNewDocument ? path : displayPath,
              value,
              format: 'editorjs-v1',
            },
          },
        });

        if (!silent) {
          toaster.create({
            title: 'Success',
            description: isNewDocument
              ? 'Document created successfully'
              : 'Document saved successfully',
          });
        }

        const savedValueString = data?.upsertDocument?.document?.value ?? value;
        onAfterSave(savedValueString);

        await refetchInTransition(refetch);

        if (isNewDocument) {
          navigate(`/documents/${path}`, { replace: true });
        }
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        saveInFlightRef.current = false;
      }
    },
    [
      documentId,
      path,
      displayPath,
      isNewDocument,
      getCurrentValue,
      upsertDocument,
      refetch,
      navigate,
      onAfterSave,
    ],
  );

  useEffect(() => {
    hasChangesRef.current = hasChanges;
  }, [hasChanges]);

  useEffect(() => {
    autoSaveEnabledRef.current = autoSaveEnabled;
  }, [autoSaveEnabled]);

  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    if (!autoSaveEnabled || !hasChanges) return;
    const id = window.setTimeout(() => {
      if (
        !autoSaveEnabledRef.current ||
        !hasChangesRef.current ||
        saveInFlightRef.current
      ) {
        return;
      }
      void handleSaveRef.current({ silent: true });
    }, 5000);
    return () => window.clearTimeout(id);
  }, [autoSaveEnabled, hasChanges, dirtyEditGeneration]);

  const autoSaveToggleButton = useMemo(
    () => (
      <IconButton
        aria-label={
          autoSaveEnabled
            ? 'Disable auto-save'
            : 'Enable auto-save (5 seconds after you stop editing)'
        }
        variant={autoSaveEnabled ? 'solid' : 'ghost'}
        colorPalette={autoSaveEnabled ? 'blue' : 'gray'}
        size="sm"
        title={
          autoSaveEnabled
            ? 'Disable auto-save'
            : 'Enable auto-save (5 seconds after you stop editing)'
        }
        loading={saving}
        disabled={saving}
        onClick={() => setDocumentAutoSaveEnabled(!autoSaveEnabled)}
      >
        <FiRefreshCw />
      </IconButton>
    ),
    [autoSaveEnabled, saving, setDocumentAutoSaveEnabled],
  );

  const revertButton = useMemo(
    () => (
      <IconButton
        aria-label="Revert to last saved"
        variant="ghost"
        size="sm"
        title="Revert to last saved version"
        onClick={onRevertToSaved}
      >
        <FiRotateCcw />
      </IconButton>
    ),
    [onRevertToSaved],
  );

  const actionsContent = !isNewDocument ? (
    hasChanges ? (
      <HStack gap={2}>
        {revertButton}
        {autoSaveEnabled ? (
          autoSaveToggleButton
        ) : (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => void handleSave()}
            loading={saving}
            loadingText="Saving..."
          >
            Save
          </Button>
        )}
      </HStack>
    ) : (
      autoSaveToggleButton
    )
  ) : null;

  useEffect(() => {
    const rightControls = (
      <HStack gap={2} align="center">
        {hasChildren && (
          <DocumentChildrenButton
            parentPath={displayPath}
            items={childrenItems}
            hasMore={childrenHasMore}
          />
        )}
        {isMobileEditorLayout && (
          <IconButton
            aria-label={
              composerFooterVisible ? 'Hide AI composer' : 'Show AI composer'
            }
            variant="ghost"
            size="sm"
            title={
              composerFooterVisible ? 'Hide AI composer' : 'Show AI composer'
            }
            onClick={onComposerFooterToggle}
          >
            {composerFooterVisible ? <FiChevronsDown /> : <FiChevronsUp />}
          </IconButton>
        )}
        <Menu.Root positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <IconButton aria-label="Document actions" variant="ghost" size="sm">
              <FiMoreVertical />
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                {documentId && (
                  <Menu.Item
                    value="messages"
                    onClick={() => setIsMessagesDrawerOpen(true)}
                  >
                    Messages
                  </Menu.Item>
                )}
                {!isNewDocument && (
                  <>
                    <Menu.Item
                      value="rename"
                      onClick={() => setIsRenameOpen(true)}
                    >
                      Rename
                    </Menu.Item>
                    <Menu.Item value="history" onClick={onNavigateToHistory}>
                      History
                    </Menu.Item>
                    {documentId && (
                      <Menu.Item
                        value="advanced"
                        onClick={() => setIsAdvancedModalOpen(true)}
                      >
                        Advanced
                      </Menu.Item>
                    )}
                  </>
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
        {isNewDocument && (
          <HStack gap={2}>
            {hasChanges && revertButton}
            {(autoSaveEnabled || !hasChanges) && autoSaveToggleButton}
            {hasChanges && !autoSaveEnabled && (
              <Button
                size="sm"
                colorScheme="blue"
                onClick={() => void handleSave()}
                loading={saving}
                loadingText="Saving..."
              >
                Save
              </Button>
            )}
          </HStack>
        )}
      </HStack>
    );

    const titleContent = (
      <HStack
        gap={4}
        align="center"
        justify="space-between"
        width="100%"
        minW={0}
      >
        <Box flex="1" minW={0}>
          <DocumentBreadcrumb
            path={displayPath}
            placeholderTitleMuted={editorPlaceholderMuted}
          />
        </Box>
        {rightControls}
      </HStack>
    );
    setTitle(titleContent);

    return () => setTitle(null);
  }, [
    childrenItems,
    childrenHasMore,
    displayPath,
    document,
    documentId,
    documentPathForRename,
    formattedName,
    getCurrentValue,
    hasChildren,
    isNewDocument,
    isRenameOpen,
    isMobileEditorLayout,
    composerFooterVisible,
    onComposerFooterToggle,
    onNavigateToHistory,
    handleRename,
    handleSave,
    hasChanges,
    editorPlaceholderMuted,
    onRevertToSaved,
    saving,
    setTitle,
    autoSaveEnabled,
    autoSaveToggleButton,
    revertButton,
  ]);

  return (
    <>
      {actionsRef.current &&
        actionsContent &&
        createPortal(actionsContent, actionsRef.current)}

      {documentId && (
        <DocumentMessagesDrawer
          documentId={documentId}
          open={isMessagesDrawerOpen}
          onOpenChange={setIsMessagesDrawerOpen}
        />
      )}

      {!isNewDocument && (
        <DialogRoot
          open={isRenameOpen}
          onOpenChange={(e) => setIsRenameOpen(e.open)}
        >
          <CenteredDialogContent>
            <DialogHeader>
              <DialogTitle>Rename document</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <RenameDocumentForm
                embedded
                active={isRenameOpen}
                currentPath={documentPathForRename}
                getCurrentValue={getCurrentValue}
                onRename={handleRename}
                onRequestClose={() => setIsRenameOpen(false)}
              />
            </DialogBody>
          </CenteredDialogContent>
        </DialogRoot>
      )}

      <DialogRoot
        open={isAdvancedModalOpen}
        onOpenChange={(e) => setIsAdvancedModalOpen(e.open)}
      >
        <CenteredDialogContent>
          <DialogHeader>
            <DialogTitle>Advanced</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={5} align="stretch">
              <Box>
                <Text fontSize="sm" color="fg.muted" mb={2}>
                  Document ID
                </Text>
                <HStack gap={2} align="flex-start">
                  <Text
                    fontFamily="mono"
                    fontSize="sm"
                    color="fg"
                    wordBreak="break-all"
                    flex={1}
                  >
                    {documentId ?? '—'}
                  </Text>
                  {documentId && (
                    <IconButton
                      size="sm"
                      variant="ghost"
                      aria-label="Copy document ID"
                      title="Copy to clipboard"
                      onClick={() => {
                        void navigator.clipboard
                          .writeText(documentId)
                          .then(() => {
                            toaster.create({
                              title: 'Copied',
                              description: 'Document ID copied to clipboard',
                              type: 'success',
                            });
                          })
                          .catch(() => {
                            toaster.create({
                              title: 'Copy failed',
                              description: 'Could not copy to clipboard',
                              type: 'error',
                            });
                          });
                      }}
                    >
                      📋
                    </IconButton>
                  )}
                </HStack>
              </Box>

              <Box>
                <HStack
                  gap={2}
                  align="flex-start"
                  justify="space-between"
                  mb={2}
                >
                  <Text fontSize="sm" color="fg.muted">
                    Raw value
                  </Text>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label="Copy raw value"
                    title="Copy raw value to clipboard"
                    disabled={!rawValueSnapshot}
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(rawValueSnapshot)
                        .then(() => {
                          toaster.create({
                            title: 'Copied',
                            description: 'Raw value copied to clipboard',
                            type: 'success',
                          });
                        })
                        .catch(() => {
                          toaster.create({
                            title: 'Copy failed',
                            description: 'Could not copy to clipboard',
                            type: 'error',
                          });
                        });
                    }}
                  >
                    📋
                  </IconButton>
                </HStack>

                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor="border"
                  overflowY="auto"
                  maxH="280px"
                  p={2}
                >
                  <Text
                    as="pre"
                    fontFamily="mono"
                    fontSize="xs"
                    color="fg"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                    m={0}
                  >
                    {rawValueSnapshot || '—'}
                  </Text>
                </Box>
              </Box>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline">Close</Button>
            </DialogActionTrigger>
          </DialogFooter>
        </CenteredDialogContent>
      </DialogRoot>

      <DialogRoot
        open={isCreateSubpageOpen}
        onOpenChange={(e) => onCreateSubpageOpenChange(e.open)}
      >
        <CenteredDialogContent>
          <DialogHeader>
            <DialogTitle>Create subpage</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" color="fg.muted">
                Enter the next part of the path for a child of{' '}
                <Text as="span" fontWeight="semibold" color="fg">
                  {displayPath || '/'}
                </Text>
                .
              </Text>
              <Input
                placeholder={
                  displayPath
                    ? 'e.g. new-page or nested/page'
                    : 'e.g. my-doc or folder/doc'
                }
                value={createSubpageSegment}
                onChange={(e) => setCreateSubpageSegment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    void handleCreateSubpage();
                  }
                  if (e.key === 'Escape') {
                    onCreateSubpageOpenChange(false);
                    setCreateSubpageSegment('');
                  }
                }}
                size="sm"
                autoFocus
                aria-invalid={!!trimmedSegment && !isCreateSubpagePathValid}
                borderColor={
                  trimmedSegment && !isCreateSubpagePathValid
                    ? 'red.500'
                    : undefined
                }
              />
              {trimmedSegment && !isCreateSubpagePathValid && (
                <Text fontSize="xs" color="red.500">
                  {DOCUMENT_PATH_DESCRIPTION}
                </Text>
              )}
            </VStack>
          </DialogBody>
          <DialogFooter>
            <HStack gap={2} justify="flex-end" width="100%">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onCreateSubpageOpenChange(false);
                  setCreateSubpageSegment('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={() => {
                  void handleCreateSubpage();
                }}
                disabled={!isCreateSubpagePathValid}
              >
                Create
              </Button>
            </HStack>
          </DialogFooter>
        </CenteredDialogContent>
      </DialogRoot>
    </>
  );
}
